import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import {
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED,
} from 'common/applicationAssemblyLineConstants';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';

import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrBox from 'client/components/SrBox';
import SrModalV2 from 'client/components/SrModalV2/SrModalV2';
import SrInfoTile, { SR_INFO_TILE_THEME_ADMIN_GREY } from 'client/components/SrInfoTile/SrInfoTile';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrButton, { SR_BUTTON_THEME_ADOBE_GREY } from 'client/components/SrButton/SrButton';

import { assemblyLineApplicationStatusOverrideFieldgroup } from 'server/models/AssemblyLineApplication/fieldgroups';

const getFormFields = ({ fields, onChange }) => {
  return assemblyLineApplicationStatusOverrideFieldgroup
    .filter((id) => fields.includes(id))
    .map((id) => {
      return {
        id: id,
        type: 'select',
        properties: {
          enableResetOption: true,
          choices: [
            {
              label: 'Not started',
              value: SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED,
            },
            {
              label: 'In progress',
              value: SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS,
            },
            {
              label: 'Not Qualified',
              value: SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED,
            },
            {
              label: 'Qualified',
              value: SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED,
            },
          ],
          onChange: (e) => onChange(id, e.target.value),
        },
        validations: ['required'],
      };
    });
};

const SrApplicationAssemblyLineEditStatusModal = ({
  fields,
  updateAssemblyLineApplication,
  assemblyLineApplication,
  onClose,
  editAppLink,
}) => {
  const _onSubmit = useCallback(
    (values) => {
      updateAssemblyLineApplication({
        assemblyLineApplicationId: assemblyLineApplication.id,
        updates: values,
      });

      onClose();
    },
    [updateAssemblyLineApplication, assemblyLineApplication, onClose],
  );

  const onChange = (id, value) => {
    _onSubmit({
      [id]: value,
    });
  };

  return (
    <Formik
      validate={(values) => {
        const formFields = getFormFields({ values, fields });
        return createFormValidationFn(formFields)(values);
      }}
      initialValues={createInitialValues({
        formFields: getFormFields({ fields }),
        defaultValues: assemblyLineApplication,
      })}
      onSubmit={_onSubmit}
      render={(formikBag) => {
        const formFields = getFormFields({
          values: formikBag.values,
          fields,
          onChange,
        });

        return (
          <SrModalV2
            buttonTheme={SR_BUTTON_THEME_ADOBE_GREY}
            showCloseButton={false}
            onClose={onClose}
            padding="0px"
            centerContent
          >
            <SrBox
              padding="40px"
              background="white"
              borderRadius="24px"
              width="759px"
              height="414px"
              margin="auto"
              boxSizing="border-box"
            >
              <SrText
                font="greycliff"
                paddingBottom="15px"
                lineHeight="33px"
                size="26"
                weight="bolder"
                align="center"
                display="block"
              >
                Edit
              </SrText>
              <SrBox marginTop="20px" />
              <SrInfoTile
                theme={SR_INFO_TILE_THEME_ADMIN_GREY}
                label="Status"
                value={
                  <SrBox marginTop="-16px">
                    <Form className="form">
                      {formFields.map((formField) => (
                        <SrFormikField
                          key={formField.id}
                          formField={formField}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      ))}
                    </Form>
                  </SrBox>
                }
              />
              <SrBox marginTop="32px" />
              <SrInfoTile
                theme={SR_INFO_TILE_THEME_ADMIN_GREY}
                label="Renter Application"
                value={
                  <SrBox marginTop="5px">
                    <SrLink to={editAppLink} target="_blank">
                      <SrText color="blue300" font="greycliff" weight="800">
                        Edit Application
                      </SrText>
                    </SrLink>
                  </SrBox>
                }
              />
              <SrBox marginTop="64px" />
              <SrButton theme={SR_BUTTON_THEME_ADOBE_GREY} onClick={onClose}>
                Close
              </SrButton>
            </SrBox>
          </SrModalV2>
        );
      }}
    />
  );
};

SrApplicationAssemblyLineEditStatusModal.propTypes = {
  fields: PropTypes.array.isRequired,
  assemblyLineApplication: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  updateAssemblyLineApplication: PropTypes.func.isRequired,
  editAppLink: PropTypes.string.isRequired,
};

SrApplicationAssemblyLineEditStatusModal.displayName = 'SrApplicationAssemblyLineEditStatusModal';

export default SrApplicationAssemblyLineEditStatusModal;
